import {
	faExclamationTriangle,
	faHome,
	faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import capmoLogo from "../../../assets/images/capmo_logo.svg";
import { AgGridAtom, ModalAtom } from "../../../components/atoms/atom";
import {
	StatusCellRenderer,
} from "../../../components/atoms/grid-cell-renderers";
import { FilterBar } from "../../../components/molecule/molecule";
import ProjectHeader from "../../../components/ProjectHeader";
import NoPermissionAlert from "../../../components/NoPermissionAlert";
import useProjectPermission from "../../../hooks/useProjectPermission";
import { API_URL } from "../../../constant/url";
import LV_STATUS from "../../../constants/lv-status";
import {
	useDeleteLVMutation,
	useGetProjectLVsQuery,
} from "../../../store/services/lvService";
import { useGetProjectTicketCategoriesQuery } from "../../../store/services/ticketCategoriesService";
import {
	selectTicketCategoryFilter,
	setTicketCategoryFilter
} from "../../../store/slices/sharedFilterSlice";

import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { createActionColumn } from "../../../utils/grid-columns";
import {
	formatCurrencyToLocale,
	getFormattedDate,
	showToast,
} from "../../../utils/method";
import { searchData } from "../../../utils/search";
import AddLVModal from "./add-lv-modal";
import EditLVModal from "./edit-lv-modal";
import ExportGaebModal from "./export-gaeb-modal";
import "./project-lv.scss";
import UploadLVModal from "./upload-lv-modal";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";

// Matches CAPMO-generated plan webhook entries (not real Nachträge) that should be deletable.
// Example: plan_approval_e949641e-3897-11f1-ad1b-b7b9ac54cc8b_f25bbdd8-ba43-4111-8d62-9b547bdde524
const CAPMO_PLAN_APPROVAL_REGEX =
	/^plan_approval_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const getLVColumns = (
	t,
	onEdit,
	onDelete,
	onDownloadExcel,
	onDownloadGaeb,
	onView,
	onUploadLv,
	onAssignCostGroups,
	onOpenBoQEditor,
	onExportGaeb,
	canEditLV = false,
) => [
		{
			field: "updatedAt",
			headerName: t("lv.updated"),
			headerTooltip: t("lv.updated"),
			unSortIcon: true,
			valueFormatter: (params) => getFormattedDate(params.value),
			maxWidth: 130,
		},
		{
			field: "isFromCapmo",
			headerName: t("common.source"),
			headerTooltip: t("common.source"),
			maxWidth: 60,
			cellRenderer: (params) => {
				const { isNachtraege, capmoChangesRejected, isFromCapmo, isLocal } = params.data;
				if (!isNachtraege) {
					return null;
				}

				// Show CAPMO logo if from CAPMO
				if (isFromCapmo) {
					return (
						<div className="capmo-logo-wrapper">
							<img
								src={capmoLogo}
								alt="CAPMO"
								height="20"
								width="20"
								title={
									capmoChangesRejected
										? t(`lv.${capmoChangesRejected}`)
										: t("lv.from-capmo")
								}
								className="d-inline-block capmo-logo"
							/>
							{capmoChangesRejected && (
								<span
									className={`capmo-status-icon ${capmoChangesRejected === "NACHTRAEGE_DELETED" ? "status-icon-deleted" : "status-icon-error"}`}
									title={t(`lv.${capmoChangesRejected}`)}
								>
									{capmoChangesRejected === "NACHTRAEGE_DELETED" ? (
										<FontAwesomeIcon icon={faTrash} />
									) : (
										<FontAwesomeIcon icon={faExclamationTriangle} />
									)}
								</span>
							)}
						</div>
					);
				}

				// Show home icon if local
				if (isLocal) {
					return (
						<FontAwesomeIcon
							icon={faHome}
							title={t("projects.local-project")}
							className="lv-home-icon"
						/>
					);
				}

				return null;
			},
		},
		{
			field: "ticketCategory.name",
			headerName: t("lv.ticket-category"),
			headerTooltip: t("lv.ticket-category"),
			unSortIcon: true,
			maxWidth: 200,
		},

		{
			field: "name",
			headerName: t("lv.lv-name"),
			headerTooltip: t("lv.lv-name"),
			unSortIcon: true,
			wrapText: true,
			cellRenderer: (params) => {
				const { isNachtraege, name, key, capmoChangesRejected } = params.data;
				const displayName = !isNachtraege
					? (name || "")
					: ([key, name].filter(Boolean).join(" - ") || "");
				return (
					<div className="lv-name-cell" title={displayName}>
						<span className="lv-name-cell__text">{displayName}</span>
						{capmoChangesRejected && (
							<div className={`lv-capmo-status-text ${capmoChangesRejected === "NACHTRAEGE_DELETED" ? "status-icon-deleted" : "status-icon-error"}`}><em>{t(`lv.${capmoChangesRejected}`)}</em></div>
						)}
					</div>
				);
			}
		},
		{
			headerName: t("invoice.amount-information"),
			field: "amountInfo",
			sortable: false,
			type: "numericColumn",
			minWidth: 180,
			maxWidth: 250,
			cellRenderer: (params) => {
				const {
					isNachtraege,
					statusCode,
					hasGaebData,
					totalPrice,
					totalPriceGross,
					cost,
					isLocal,
				} = params.data;
				const lvAmount = formatCurrencyToLocale(cost || 0);
				const openAmountKey = isLocal ? "lv.open-amount-local" : "lv.open-amount";
				const confirmedAmountKey = isLocal ? "lv.confirmed-amount-local" : "lv.confirmed-amount";

				if (isNachtraege && !hasGaebData) {
					const labelKey = statusCode === LV_STATUS.APPROVED ? confirmedAmountKey : openAmountKey;
					return (
						<div className="lv-amount-cell">
							<div className="lv-amount-cell__row">
								<span className="lv-amount-cell__label">{t(labelKey)}</span>
								<span className="lv-amount-cell__value lv-amount-cell__value--primary">{lvAmount}</span>
							</div>
						</div>
					);
				}

				const netAmount = formatCurrencyToLocale(totalPrice || 0);
				const grossAfterDiscount = formatCurrencyToLocale(totalPriceGross || 0);
				return (
					<div className="lv-amount-cell">
						<div className="lv-amount-cell__row">
							<span className="lv-amount-cell__label">{t("lv.net")}</span>
							<span className="lv-amount-cell__value lv-amount-cell__value--primary">{netAmount}</span>
						</div>
						<div className="lv-amount-cell__row">
							<span className="lv-amount-cell__label">{t("invoice.gross")}</span>
							<span className="lv-amount-cell__value">{grossAfterDiscount}</span>
						</div>
						{isNachtraege && (
							<div className="lv-amount-cell__row">
								<span className="lv-amount-cell__confirmed">
									{t(confirmedAmountKey)}: {lvAmount}
								</span>
							</div>
						)}
					</div>
				);
			},
		},
		{
			field: "isNachtraege",
			headerName: t("lv.nachtraege"),
			headerTooltip: t("lv.nachtraege"),
			unSortIcon: false,
			cellRenderer: (params) => (params.value ? "✓" : ""),
			maxWidth: 120,
		},
		{
			headerName: t("lv.status.title"),
			field: "statusCode",
			sortable: false,
			cellRenderer: StatusCellRenderer,
			minWidth: 160,
			maxWidth: 220,
			cellRendererParams: {
				onEditClick: onEdit,
				onOpenBoQEditor,
				canEdit: canEditLV,
				t,
			},
		},
		createActionColumn({
			t,
			actions: [
				{
					projectPermission: { area: PROJECT_AREA.LV, action: PROJECT_ACTION.EDIT },
					onClick: onEdit,
					label: "help.action-edit",
					show: (data) => data.isNachtraege && !data.isFromCapmo,
					disabled: (data) => data.hasInvoice,
					disabledTooltip: "lv.cannot-edit-has-invoice",
				},
				{
					projectPermission: { area: PROJECT_AREA.LV, action: PROJECT_ACTION.EXPORT },
					onClick: onDownloadExcel,
					label: "lv.action-download-excel",
					show: (data) => Boolean(data?.id && data?.hasExcelFile),
				},
				{
					projectPermission: { area: PROJECT_AREA.LV, action: PROJECT_ACTION.EXPORT },
					onClick: onDownloadGaeb,
					label: "lv.action-download-gaeb",
					show: (data) => Boolean(data?.id && data?.hasGaebFile),
				},
				{
					onClick: onExportGaeb,
					label: "lv.action-export-gaeb-phase",
					projectPermission: { area: PROJECT_AREA.LV, action: PROJECT_ACTION.EXPORT },
					// Only available for LVs that were finalised through the BoQ Editor
					show: (data) => data.boqStatus === "finalized",
				},
				{
					projectPermission: { area: PROJECT_AREA.LV, action: PROJECT_ACTION.UPLOAD },
					onClick: onUploadLv,
					label: "lv.action-upload-lv",
					// show: (data) =>
					// 	!data.isNachtraege ||
					// 	(data.isNachtraege && data.statusCode === LV_STATUS.APPROVED),
					disabled: (data) => data.hasInvoice,
					disabledTooltip: "lv.cannot-upload-has-invoice",
				},
				{
					projectPermission: { area: PROJECT_AREA.LV, action: PROJECT_ACTION.EDIT },
					onClick: onAssignCostGroups,
					label: "lv.action-assign-cost-groups",
					show: (data) => data.hasPositions,
				},
				{
					projectPermission: { area: PROJECT_AREA.LV, action: PROJECT_ACTION.EDIT },
					onClick: onOpenBoQEditor,
					label: "lv.action-open-boq-editor",
					disabled: (data) => data.hasInvoice,
					disabledTooltip: "lv.cannot-edit-has-invoice",
				},
				{
					projectPermission: { area: PROJECT_AREA.LV, action: PROJECT_ACTION.VIEW },
					onClick: onView,
					label: "help.action-view",
					show: (data) => data.hasGaebData,
				},
				{
					projectPermission: { area: PROJECT_AREA.LV, action: PROJECT_ACTION.DELETE },
					onClick: onDelete,
					label: "help.action-delete",
					danger: true,
					show: (data) => !data.isNachtraege || (data.isNachtraege && !data.isFromCapmo) || CAPMO_PLAN_APPROVAL_REGEX.test(data.name),
					disabled: (data) => data.hasInvoice,
					disabledTooltip: "lv.cannot-delete-has-invoice",
				},
			]
		}),
	];

function ProjectLVPage() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const projectDetails = useSelector(selectProjectDetails);
	const { id: projectId } = projectDetails || {};

	const { data: categoriesResponse } = useGetProjectTicketCategoriesQuery(
		{ projectId },
		{ skip: !projectId },
	);
	const categories = useMemo(() => categoriesResponse?.items ?? [], [categoriesResponse]);
	const {
		data: lvsResponse,
		isLoading: loading,
	} = useGetProjectLVsQuery(projectId, { skip: !projectId });
	const lvs = useMemo(() => lvsResponse?.items ?? [], [lvsResponse]);
	const [deleteLV, { isLoading: deletingLV }] = useDeleteLVMutation();

	// Use a custom selector with projectId
	const selectedTicketCategoryId = useSelector(state =>
		selectTicketCategoryFilter(state, projectId)
	) || "";

	const {
		can,
		isLoading: permissionLoading,
		filterByCategoryScope,
	} = useProjectPermission();
	const canViewLv = !permissionLoading && can(PROJECT_AREA.LV, PROJECT_ACTION.VIEW);
	const canEditLV = can(PROJECT_AREA.LV, PROJECT_ACTION.EDIT);
	const canCreateLv = can(PROJECT_AREA.LV, PROJECT_ACTION.CREATE);
	const scopedCategories = useMemo(
		() => filterByCategoryScope(categories, (item) => item.id),
		[filterByCategoryScope, categories],
	);

	const [filteredLVList, setFilteredLVList] = useState([]);
	const [showAddLVModal, setShowAddLVModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [selectedLV, setSelectedLV] = useState(null);

	// Add state for uploading LV file to existing change order
	const [showUploadLVModal, setShowUploadLVModal] = useState(false);
	const [lvToUpload, setLvToUpload] = useState(null);

	// Add state for editing LV
	const [showEditLVModal, setShowEditLVModal] = useState(false);
	const [lvToEdit, setLvToEdit] = useState(null);

	// GAEB phase export (BoQ Editor-finalized LVs only)
	const [showExportGaebModal, setShowExportGaebModal] = useState(false);
	const [lvToExport, setLvToExport] = useState(null);

	// BoQ overwrite warning
	const [showBoqOverwriteModal, setShowBoqOverwriteModal] = useState(false);
	const [pendingUploadLV, setPendingUploadLV] = useState(null);

	const [filterItems, setFilterItems] = useState(
		getSearchAddFilter({ addTextKey: "lv.add-lv" }, [
			{
				key: "ticketCategory",
				type: "dropdown",
				labelKey: "lv.ticket-category",
				options: [],
				value: "",
			},
			{
				key: "downloadSample",
				type: "button",
				labelKey: "lv.download-sample-excel",
			},
			{
				key: "refresh",
				type: "button",
				labelKey: "common.refresh",
				displayPosition: "right",
				className: "btn btn-outline-secondary me-2",
				icon: "refresh",
			},
		]),
	);

	// LVs + categories fetched via RTK Query hooks above.

	// Update filtered LV list when LVs change
	useEffect(() => {
		let lvsArray = Array.isArray(lvs) ? lvs : [];
		if (lvs && Array.isArray(lvs.items)) {
			lvsArray = [...lvs.items];
		}
		lvsArray = filterByCategoryScope(
			lvsArray,
			(lv) => lv.ticketCategory?.id ?? lv.projectTicketCategoryId,
		);
		setFilteredLVList(lvsArray);
	}, [lvs, filterByCategoryScope]);

	// Update ticket category filter options when categories are loaded
	useEffect(() => {
		if (scopedCategories && scopedCategories.length > 0) {
			const categoryOptions = scopedCategories.map((cat) => ({
				value: cat.id,
				label: cat.name,
			}));

			// Update the filter dropdown options
			setFilterItems((prev) => {
				const updated = [...prev];
				const categoryFilterIndex = updated.findIndex(
					(item) => item.key === "ticketCategory",
				);
				if (categoryFilterIndex !== -1) {
					updated[categoryFilterIndex].options = categoryOptions;
					updated[categoryFilterIndex].value = selectedTicketCategoryId || "";
				}
				return updated;
			});
		}
	}, [scopedCategories, t, selectedTicketCategoryId]);

	// Apply initial filtering from saved ticket category
	useEffect(() => {
		if (projectId && selectedTicketCategoryId && lvs) {
			let lvsArray = Array.isArray(lvs) ? lvs : [];
			if (lvs && Array.isArray(lvs.items)) {
				lvsArray = [...lvs.items];
			}

			if (lvsArray.length > 0) {
				// Update filter dropdown to show selected value
				setFilterItems(prev => {
					const updated = [...prev];
					const categoryFilterIndex = updated.findIndex(item => item.key === "ticketCategory");
					if (categoryFilterIndex !== -1) {
						updated[categoryFilterIndex].value = selectedTicketCategoryId;
					}
					return updated;
				});

				// Apply filtering
				const filtered = lvsArray.filter(
					lv => lv.ticketCategory && lv.ticketCategory.id === selectedTicketCategoryId
				);
				if (filtered.length > 0) {
					setFilteredLVList(filtered);
				}
			}
		}
	}, [projectId, selectedTicketCategoryId, lvs]);

	const handleEditClick = (data) => {
		setLvToEdit(data);
		setShowEditLVModal(true);
	};

	const handleDeleteClick = (data) => {
		setSelectedLV(data);
		setShowDeleteModal(true);
	};

	const handleDelete = async () => {
		try {
			if (!selectedLV || !selectedLV.id) {
				showToast(t("errors.general"), { type: "error" });
				return;
			}

			await deleteLV({ projectId, lvId: selectedLV.id }).unwrap();
			setShowDeleteModal(false);
			setSelectedLV(null);
			showToast(t("lv.delete-success"), { type: "success" });
		} catch (error) {
			showToast(t("errors.general"), { type: "error" });
		}
	};

	const downloadFile = async (url, fallbackName) => {
		try {
			const response = await fetch(url, { credentials: "include" });
			if (!response.ok) {
				showToast(t("lv.error-file-not-found"), { type: "error" });
				return;
			}
			const blob = await response.blob();
			const disposition = response.headers.get("Content-Disposition") || "";
			const match = disposition.match(/filename="?([^"]+)"?/);
			const fileName = match ? match[1] : fallbackName;
			const objectUrl = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = objectUrl;
			a.download = fileName;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(objectUrl);
		} catch {
			showToast(t("lv.error-file-not-found"), { type: "error" });
		}
	};

	const handleDownloadExcel = (data) => {
		if (!data?.id || !data?.hasExcelFile) {
			showToast(t("lv.error-file-not-found"), { type: "error" });
			return;
		}
		downloadFile(`${API_URL}projects/${projectId}/lvs/${data.id}/excel`, `${data.name || data.id}.xlsx`);
	};

	const handleDownloadGaeb = (data) => {
		if (!data?.id || !data?.hasGaebFile) {
			showToast(t("lv.error-file-not-found"), { type: "error" });
			return;
		}
		downloadFile(`${API_URL}projects/${projectId}/lvs/${data.id}/gaeb`, `${data.name || data.id}.x86`);
	};

	const handleExportGaeb = (data) => {
		setLvToExport(data);
		setShowExportGaebModal(true);
	};

	const handleView = (data) => {
		if (!data.hasGaebData) {
			return;
		}
		if (!data.id) {
			return;
		}
		navigate(`/projects/${projectId}/lv/${data.id}/view`);
	};

	const handleUploadLv = async (data) => {
		if (!data.projectTicketCategoryId) {
			alert(t("lv.error-missing-ticket-category"));
			return;
		}
		if (data.boqStatus) {
			setPendingUploadLV(data);
			setShowBoqOverwriteModal(true);
			return;
		}
		setLvToUpload(data);
		setShowUploadLVModal(true);
	};

	const handleBoqOverwriteConfirm = () => {
		setShowBoqOverwriteModal(false);
		setLvToUpload(pendingUploadLV);
		setPendingUploadLV(null);
		setShowUploadLVModal(true);
	};

	const handleOpenBoQEditor = (data) => {
		navigate(`/projects/${projectId}/lv/${data.id}/edit`);
	};

	const handleAssignCostGroups = (data) => {
		if (!data.hasGaebData) {
			showToast(t("lv.error-no-gaeb-data"), { type: "error" });
			return;
		}
		if (!data.id) {
			showToast(t("lv.error-missing-lv-id"), { type: "error" });
			return;
		}
		navigate(`/projects/${projectId}/lv/${data.id}/cost-groups`);
	};

	const lvColumns = getLVColumns(
		t,
		handleEditClick,
		handleDeleteClick,
		handleDownloadExcel,
		handleDownloadGaeb,
		handleView,
		handleUploadLv,
		handleAssignCostGroups,
		handleOpenBoQEditor,
		handleExportGaeb,
		canEditLV,
	);

	const onFilterAction = (index, key, info) => {
		const updatedFilters = [...filterItems];
		updatedFilters[index].value = info?.value;
		setFilterItems(updatedFilters);

		// Save ticket category to shared state
		if (key === "ticketCategory") {
			dispatch(setTicketCategoryFilter({
				projectId,
				ticketCategoryId: info?.value || ""
			}));
		}

		// Ensure we're working with an array before filtering
		let lvsArray = [];
		if (Array.isArray(lvs)) {
			lvsArray = [...lvs];
		} else if (lvs && Array.isArray(lvs.items)) {
			lvsArray = [...lvs.items];
		}

		let filtered = lvsArray;

		updatedFilters.forEach((item) => {
			if (item.key === "search" && item.value) {
				filtered = searchData(item.value, filtered, lvColumns);
			}

			// Add filtering by ticket category
			if (item.key === "ticketCategory" && item.value) {
				filtered = filtered.filter(
					(lv) => lv.ticketCategory.id === item.value,
				);
			}
		});
		setFilteredLVList(filtered);

		if (key === "add") {
			setShowAddLVModal(true);
		}
		if (key === "back") {
			navigate("/projects");
		}
		if (key === "downloadSample") {
			handleDownloadSampleExcel();
		}
		if (key === "refresh") {
			// RTK Query refetches via tag invalidation; no manual refetch needed.
		}
	};

	const handleDownloadSampleExcel = () => {
		// Access the file directly from public folder
		window.open(
			`${process.env.PUBLIC_URL}/samples/lv-sample.xlsx`,
			"_blank",
		);
	};

	const handleLVCreated = (newLV) => {
		// Refresh the LV list when a new LV is created
		fetchLVs();
		if (newLV?._openUpload) {
			setLvToUpload(newLV);
			setShowUploadLVModal(true);
		}
	};

	const handleLVUploaded = (updatedLV) => {
		// Refresh the LV list when an LV file is uploaded
		fetchLVs();
	};

	const fetchLVs = () => {
		// RTK invalidation auto-refetches; kept as a no-op for callsite compatibility.
	};

	if (permissionLoading) {
		return (
			<div className="project-lv-page">
				<ProjectHeader projectName={projectDetails?.name || ""} tabTitle={t("lv.lv")} />
				<div className="page-container">
					<TableSkeleton />
				</div>
			</div>
		);
	}

	if (!canViewLv) {
		return (
			<div className="project-lv-page">
				<ProjectHeader projectName={projectDetails?.name || ""} tabTitle={t("lv.lv")} />
				<NoPermissionAlert />
			</div>
		);
	}

	return (
		<div className="project-lv-page">
			<ProjectHeader projectName={projectDetails?.name || ""} tabTitle={t("lv.lv")} />
			<div className="page-container">
				<FilterBar
					items={filterItems}
					onFilterAction={onFilterAction}
					canAdd={canCreateLv}
				/>

				<div className="flex-grow-1 ag-grid-parent">
					{loading?.lvs && filteredLVList.length === 0 ? (
						<TableSkeleton />
					) : (
						<AgGridAtom
							columnDefs={lvColumns}
							rowData={filteredLVList}
							context={{
								projectCan: can,
							}}
							overlayLoadingTemplate=""
							overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${t("lv.no-lvs")}</span>`}
							defaultColDef={{
								flex: 1,
								sortable: true,
								wrapText: false,
								autoHeight: false,
							}}
							rowHeight={80}
							suppressRowTransform={true}
							getRowId={(params) => params.data.id}
						/>
					)}
				</div>

				{showAddLVModal && <AddLVModal
					show={showAddLVModal}
					onHide={() => setShowAddLVModal(false)}
					projectId={projectId}
					onSuccess={handleLVCreated}
					isLocalProject={projectDetails?.isLocal || false}
				/>}

				{showUploadLVModal && <UploadLVModal
					show={showUploadLVModal}
					onHide={() => {
						setShowUploadLVModal(false);
						setLvToUpload(null);
					}}
					projectId={projectId}
					onSuccess={handleLVUploaded}
					lvData={lvToUpload}
					isLocalProject={projectDetails?.isLocal || false}
				/>}

				{showEditLVModal && <EditLVModal
					show={showEditLVModal}
					onHide={() => {
						setShowEditLVModal(false);
						setLvToEdit(null);
					}}
					projectId={projectId}
					onSuccess={handleLVUploaded}
					lvData={lvToEdit}
					isLocalProject={projectDetails?.isLocal || false}
				/>}

				{showExportGaebModal && lvToExport && (
					<ExportGaebModal
						show={showExportGaebModal}
						onHide={() => {
							setShowExportGaebModal(false);
							setLvToExport(null);
						}}
						lvData={lvToExport}
						projectId={projectId}
					/>
				)}

				{showBoqOverwriteModal && (
					<ModalAtom
						title={t("lv.upload-overwrites-boq-title")}
						body={<p>{t("lv.upload-overwrites-boq-warning")}</p>}
						saveText={t("lv.upload-overwrites-boq-confirm")}
						show={showBoqOverwriteModal}
						handleClose={() => {
							setShowBoqOverwriteModal(false);
							setPendingUploadLV(null);
						}}
						handleSubmit={handleBoqOverwriteConfirm}
					/>
				)}

				{showDeleteModal && <ModalAtom
					title={t("lv.delete-lv")}
					body={<p>{t("lv.delete-lv-warning")}</p>}
					saveText={
						deletingLV ? t("common.loading") : "delete"
					}
					show={showDeleteModal}
					handleClose={() => {
						setShowDeleteModal(false);
						setSelectedLV(null);
					}}
					handleSubmit={handleDelete}
					disabled={deletingLV}
				/>}
			</div>
		</div>
	);
}

export default ProjectLVPage;
